<template>
  <div class="convey-connect-calls d-flex flex-column">
    <h4 class="m-0 mb-2">Call Activities</h4>
    <div style="flex: 1;">
      <ConveyTable
        showSearch
        showFilter
        showDateRange
        showExport
        tableClassName="convey-connect-calls-table"
        :items="conveyConnectCalls"
        :fields="fields"
        :placeholders="placeholders"
        :filterFields="filterFields"
        :filterOptions="filterOptions"
        :sortBy="sortBy"
        :sortDesc="sortDesc"
        :perPageOptions="perPageOptions"
        :perPage="perPage"
        :currentPage="currentPage"
        :totalRows="totalRows"
        :isLoading="isLoading"
        :isExportProcessing="isExportProcessing"
        @onFetch="(options) => fetchCalls(options)"
        @onExport="(options) => exportToCsv(options)"
      >
        <template #cell(callID)="data">
          <span class="fw-bold">{{ data.item.id }}</span>
        </template>
        <template #cell(status)="data">
          <span
            :class="`convey-connect-calls-table--${
              data.value
            }-text badge font-weight-medium text-capitalize ${
              isDarkMode ? 'dark' : ''
            }`"
            >{{ data.value }}</span
          >
        </template>
        <template #cell(channel)="data">
          <span
            class="text-capitalize fw-bold"
            :class="`convo-archived badge`"
            >{{ data.value }}</span
          >
        </template>
        <template #cell(record_timestamp)="data">
          <span>{{
            data.item.createdTime
              ? formatDate(
                  new Date(data.item.createdTime).getTime(),
                  "MMM DD, YYYY h:mm a"
                )
              : ""
          }}</span>
        </template>
        <template #cell(requester)="data">
          <span class="text-capitalize d-block fw-bold"
            >{{ data.item.requestorName }}
          </span>
        </template>
        <template #cell(interpreter)="data">
          <span class="text-capitalize d-block fw-bold"
            >{{ data.item.interpreterName }}
          </span>
          <span>{{ data.item.interpreterNumber }}</span>
        </template>
        <template #cell(type)="data">
          <span class="text-capitalize">{{ data.value }}</span>
        </template>
        <template #cell(service)="data">
          <span class="text-capitalize fw-bold">{{ data.value }}</span>
        </template>
        <template #cell(rating)="data">
          <span class="text-capitalize fw-bold"
            >{{ data.value ? data.value : 0 }} / 5</span
          >
        </template>
        <template #cell(cost)="data">
          ${{ data.item.invoiceAmount ? data.item.invoiceAmount : 0 }}
        </template>
        <template #cell(action)="data">
          <b-button
            v-b-modal.modal-center
            variant="text"
            @click="viewDetail(data.item)"
            ><b-icon icon="eye-fill"></b-icon
          ></b-button>
        </template>
      </ConveyTable>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from "vuex";
import { EventBus } from "@/events/eventBus";
import formatter from "@/mixins/formatter";
import errorHandler from "@/mixins/errorHandler";
import moment from "moment";
import axios from "axios";
import {
  runGraphqlQuery,
  formatDuration,
  downloadCSV,
  getBaseGraphqlServerUrl,
} from "../../../graphql/utils";
import { recentCallsquery } from "../../../graphql/queries";
import CallActivitiesDetails from "./CallActivitiesDetails.vue";
import ConveyTable from "@/components/ConveyTable";

export default {
  name: "CallActivities",
  mixins: [formatter, errorHandler],
  components: { ConveyTable },
  data() {
    return {
      conveyConnectCalls: [],
      fields: [
        { key: "callID", sortable: false },
        { key: "status", sortable: true },
        { key: "record_timestamp", sortable: true, label: "Time" },
        { key: "interpreter", sortable: false },
        { key: "requester", sortable: false },
        { key: "rating", sortable: false },
        { key: "type", sortable: false },
        { key: "cost", sortable: false },
        { key: "duration", sortable: false },
        { key: "service", sortable: false },
        { key: "action", sortable: false, stickyColumn: true, class: "right-sticky-column actions-column" },
      ],
      perPage: 10,
      perPageOptions: [5, 10, 25, 50, 100],
      currentPage: 1,
      totalRows: 0,
      isLoading: false,
      isExportProcessing: false,
      sortBy: "record_timestamp",
      sortDesc: true,
      filterOptions: {
        type: [
          { value: "1", text: "Audio", label: "Audio" },
          { value: "0",text: "Video", label: "Video" },
        ],
        status: [
          { value: "Connected", text: "Connected", label: "Connected" },
          { value: "Abandoned", text: "Abandoned", label: "Abandoned" },
        ],
      },
      filterFields: [
        { name: "type", label: "Type" },
        { name: "status", label: "Status" },
      ],
      placeholders: {
        search: "Search by call Id, interpreter, status and requester",
        daterange: "Select Date Range",
      },
      fetchAbortController: null,
    };
  },
  computed: {
    ...mapGetters("user", ["currentToken", "isDarkMode"]),
    ...mapGetters("agency", ["currentAgencyId"]),
  },
  methods: {
    viewDetail(data) {
      const $ = window.$;
      $("#id-modal-container").modal({
        backdrop: "static"
      });
      EventBus.$emit("open", {
        component: CallActivitiesDetails,
        title: "Call Activity Detail",
        closeOnClick: true,
        centered: true,
        props: {
          callActivityData: data
        },
      });
    },
    getDuration(data) {
      return formatDuration(data.duration);
    },
    abortFetchOperation() {
      if (this.fetchAbortController) {
        this.fetchAbortController.abort();
        this.fetchAbortController = null;
      }
    },
    async fetchCalls(fetchOptions) {
      try {
        this.abortFetchOperation();
        this.fetchAbortController = new AbortController();
        const {
          perPage,
          currentPage,
          searchKey,
          filter,
          startDate,
          endDate,
          sortBy,
          sortDesc,
        } = fetchOptions;
        this.isLoading = true;
        this.currentPage = currentPage;
        this.perPage = perPage;
        this.sortBy = sortBy;
        this.sortDesc = sortDesc;
        const offset = (currentPage - 1) * perPage;

        const query = recentCallsquery({
          startDate,
          endDate,
          offset,
          searchKey,
          orderBy: sortBy,
          sortDesc: sortDesc ? "DESC" : "ASC",
          perPage,
          filter,
        });

        const res = await runGraphqlQuery(query, this.fetchAbortController.signal);
        const responseData = res.data.data.getConveyConnectCallHistory;
        this.totalRows = Number(responseData.total);
        this.conveyConnectCalls = responseData.data;
      } catch (err) {
        if (!this.isApiCanceled(err)) {
          console.log("[CallActivities:fetchCalls] ERROR: ", err);
        }
      } finally {
        this.isLoading = false;
      }
    },
    async exportToCsv(exportOptions) {
      try {
        const {
          searchKey,
          startDate,
          endDate,
          perPage,
          currentPage,
          sortBy,
          sortDesc,
          filter
        } = exportOptions;
        this.isExportProcessing = true;
        const baseURL = getBaseGraphqlServerUrl();
        const exportCallsUrl = `${baseURL}/export-recent-calls`;

        const requestBody = JSON.stringify({
          search_key: searchKey,
          filters: filter,
          start_date: startDate,
          end_date: endDate,
          sort_by_field: sortBy,
          sort_by_order: sortDesc ? "DESC" : "ASC",
        });

        const response = await axios.post(exportCallsUrl, requestBody, {
          responseType: "blob",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${this.currentToken}`,
            "x-agency-id": `${this.currentAgencyId}`,
            "Content-Type": "application/json",
          },
        });
        const blob = new Blob([response.data], { type: "text/csv" });
        this.isExportProcessing = false;
        downloadCSV(blob, "interpreter-call-activities");
      } catch (error) {
        this.isExportProcessing = false;
      }
    }
  }
};
</script>

<style lang="scss">
.convey-connect-calls {
  width: 100%;
  height: 100%;
  overflow: hidden;

  .convey-connect-calls-table {
    &--agentPhone {
      color: #667085;
      font-weight: 400;
      font-size: 0.875rem;
      display: block;
      &.dark {
        a {
          color: #9dc7ff !important;
        }
      }
    }
    &--Connected-text {
      color: #0d894f;
      font-size: 0.875rem;
      background: #e7f4ee;
      font-weight: 500;
      padding: 4px 12px 4px 12px;
      border-radius: 8px;
      &.dark {
        color: #0d894f;
        background: #dae5e0;
      }
    }
    &--Abandoned-text {
      color: #e46a11;
      font-size: 0.875rem;
      background: #fdf1e8;
      font-weight: 500;
      padding: 4px 12px 4px 12px;
      border-radius: 8px;
      &.dark {
        color: #ba490f;
        background: #e7cab4;
      }
    }
  }
}
</style>
